import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, MenuItem, Select, styled, FormControl, Typography } from '@mui/material';
import { fonts } from '@theme/fontsCustomer';
import { useGetPrivacyPolicy, useGetPrivacyPolicyList } from '@queryHooks/usePrivacyPolicy';

export const PrivacyPolicyDetail = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { version: versionParam } = useParams<{ version: string }>();
  const [selectedVersion, setSelectedVersion] = useState<number | undefined>(
    versionParam ? Number(versionParam) : undefined,
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string>(i18n.language);

  const { data: privacyPolicyList } = useGetPrivacyPolicyList();

  // 최신 버전 자동 선택
  useEffect(() => {
    if (!versionParam && privacyPolicyList) {
      const latestPolicy = privacyPolicyList.find(policy => policy.isLatest);
      if (latestPolicy) {
        setSelectedVersion(latestPolicy.version);
      }
    } else if (versionParam) {
      setSelectedVersion(Number(versionParam));
    }
  }, [versionParam, privacyPolicyList]);

  const { data: privacyPolicy, isLoading } = useGetPrivacyPolicy(selectedVersion ?? 0, selectedLanguage);

  const handleVersionChange = (version: number) => {
    setSelectedVersion(version);
    navigate(`/privacy-policy/${version}`);
  };

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
  };

  return (
    <Container>
      <Header>
        <Title>{t('PrivacyPolicyPage.detail.title')}</Title>
        <ControlWrapper>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={selectedLanguage}
              onChange={e => handleLanguageChange(e.target.value)}
              renderValue={selected => (i18n.language === 'en' ? selected : selected === 'en' ? '영어' : '한국어')}
            >
              <MenuItem value="en" selected={selectedLanguage === 'en'}>
                {selectedLanguage === 'en' && '✓ '}
                {i18n.language === 'en' ? 'en' : '영어'}
              </MenuItem>
              <MenuItem value="ko" selected={selectedLanguage === 'ko'}>
                {selectedLanguage === 'ko' && '✓ '}
                {i18n.language === 'en' ? 'ko' : '한국어'}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={selectedVersion ?? ''}
              onChange={e => handleVersionChange(Number(e.target.value))}
              displayEmpty
              renderValue={selected => {
                if (!selected) return t('PrivacyPolicyPage.detail.selectVersion');
                const policy = privacyPolicyList?.find(p => p.version === selected);
                return policy
                  ? `v${policy.version} (${policy.formattedEffectiveDate})`
                  : t('PrivacyPolicyPage.detail.selectVersion');
              }}
            >
              {privacyPolicyList?.map(policy => (
                <MenuItem key={policy.version} value={policy.version} selected={selectedVersion === policy.version}>
                  {selectedVersion === policy.version && '✓ '}v{policy.version} ({policy.formattedEffectiveDate})
                  {policy.isLatest && ` - ${t('PrivacyPolicyPage.detail.latestVersion')}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </ControlWrapper>
      </Header>

      <ContentWrapper>
        {isLoading ? (
          <LoadingWrapper>
            <CircularProgress />
            <LoadingText>{t('PrivacyPolicyPage.detail.loading')}</LoadingText>
          </LoadingWrapper>
        ) : privacyPolicy ? (
          <StyledIframe srcDoc={privacyPolicy.content} title={privacyPolicy.title} sandbox="allow-scripts" />
        ) : (
          <ErrorWrapper>
            <ErrorText>{t('PrivacyPolicyPage.detail.error')}</ErrorText>
          </ErrorWrapper>
        )}
      </ContentWrapper>
    </Container>
  );
};

const Container = styled(Box)`
  width: 100%;
  height: calc(100vh - var(--manager-gnb-height));
  overflow: auto;
  display: flex;
  flex-direction: column;
  padding: 28px 32px;
`;

const Header = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled('strong')`
  ${fonts.Headline5};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const ControlWrapper = styled(Box)`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ContentWrapper = styled(Box)`
  flex: 1;
  background-color: ${({ theme: { colors } }) => colors['bg-white']};
  border-radius: 8px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  position: relative;
`;

const StyledIframe = styled('iframe')`
  width: 100%;
  height: 100%;
  border: none;
`;

const LoadingWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
`;

const LoadingText = styled(Typography)`
  ${fonts.Body1};
  color: ${({ theme: { colors } }) => colors['text-gray-light']};
`;

const ErrorWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const ErrorText = styled(Typography)`
  ${fonts.Body1};
  color: ${({ theme: { colors } }) => colors['text-red-dark']};
`;
